import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react';

import { errorMessage } from '@/shared/api/errors';
import {
  CATEGORIES,
  CATEGORY_LABELS,
  DESTINATION_LABELS,
  type EmailCategory,
  type TriageDestination,
} from '@/shared/triage/vocabulary';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { PlusIcon, XIcon } from '@/shared/ui/icons';
import { Spinner } from '@/shared/ui/Spinner';

import type { TriageActivation } from './types';
import { useAdminTriage, usePutDestinations, usePutFolders } from './useAdmin';

/** The draft without the empty fields, trimmed: what the API receives. */
function compact(draft: Record<string, string>): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(draft)) {
    const trimmed = value.trim();
    if (trimmed !== '') result[key] = trimmed;
  }
  return result;
}

/** Each list trimmed and without its empty fields, and a destination left with none out. */
function compactLists(draft: Record<string, string[]>): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  for (const [key, values] of Object.entries(draft)) {
    const kept = values.map((value) => value.trim()).filter((value) => value !== '');
    if (kept.length > 0) result[key] = kept;
  }
  return result;
}

/** What the back accepts per destination (contract v10.29, `MAX_ADDRESSES_PER_DESTINATION`). */
const MAX_ADDRESSES = 5;

/**
 * The three destinations that resolve to real addresses, one or several each (contract
 * v10.29). The doubt rule is two of them.
 */
const ADDRESSABLE = [
  'administracion',
  'maria',
  'paulo',
] as const satisfies readonly TriageDestination[];

/**
 * What each destination receives, in the shop's words, so an address field says what
 * will land in it. Mirrors the purchases mailbox table of `cubas.triage.routing`
 * (CONTEXTO §3.2) and its sender exceptions (`SENDER_DESTINATIONS`: Celsa's orders to
 * María since 2026-09-27), which are code in the back on purpose: a change there is a
 * change here, and the test of this screen pins the three lines.
 */
const DESTINATION_HINTS: Record<(typeof ADDRESSABLE)[number], string> = {
  administracion:
    'Facturas de proveedores, clientes que piden copia de un albarán o de una factura, y pedidos que ya traen número de albarán (la mercancía ya salió y solo falta facturar). También lo que no encaja en ningún otro tipo.',
  maria:
    'Lo que tiene que ver con el banco: confirming, anticipos de facturas y avisos de cobro o de pago. También todos los pedidos del Grupo Celsa, traigan albarán o no.',
  paulo:
    'Pedidos que hay que preparar (llegan sin número de albarán) y solicitudes de presupuesto. Si no está claro si un pedido ya salió, va a Paulo y a Administración a la vez.',
};

/**
 * Where a forwarded email goes and which folder of the mailbox each kind of email is
 * copied into. Both are documents replaced whole, so each form keeps a draft and saves
 * it in one go. Two tables, one under the other, each with a sentence on top saying what
 * it is for and a line at the bottom with the rule that is not obvious.
 */
export function DestinationsTab() {
  const triage = useAdminTriage();
  return (
    <div className="flex flex-col gap-4">
      {triage.isPending ? <Spinner variant="block" label="Cargando los destinos" /> : null}
      {triage.isError ? (
        <ErrorMessage error={triage.error}>No se pudieron cargar los destinos</ErrorMessage>
      ) : null}
      {triage.data ? (
        <>
          <DestinationsForm activation={triage.data} />
          <FoldersForm activation={triage.data} />
        </>
      ) : null}
    </div>
  );
}

/**
 * One line of a settings table: the name and what it means on the left, the field on
 * the right. A plain `<label htmlFor>`, so the name is the field's accessible name and
 * the explanation hangs off `aria-describedby`.
 */
function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: (ids: { id: string; describedBy: string | undefined }) => ReactNode;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className="border-line-2 grid gap-x-6 gap-y-1 border-b py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] sm:items-center">
      <div className="min-w-0">
        <label htmlFor={id} className="text-ink block text-[14px] font-medium">
          {label}
        </label>
        {hint ? (
          <p id={hintId} className="text-ink-3 mt-0.5 text-[12.5px] leading-snug">
            {hint}
          </p>
        ) : null}
      </div>
      {children({ id, describedBy: hint ? hintId : undefined })}
    </div>
  );
}

function SaveRow({
  label,
  pending,
  saved,
  error,
}: {
  label: string;
  pending: boolean;
  saved: boolean;
  error: unknown;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 pt-1">
      <Button type="submit" size="sm" variant="primary" disabled={pending}>
        {pending ? 'Guardando…' : label}
      </Button>
      <p role="status" className="text-ok text-[13.5px]">
        {saved ? 'Guardado.' : ''}
      </p>
      {error ? (
        <p role="alert" className="text-danger text-[13.5px]">
          {errorMessage(error)}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The addresses of one destination: one field per address, «Añadir otra dirección»
 * under them up to the back's limit, and a cross on each when there are two or more.
 * The first field is the one the row's label names; the others say which one they are,
 * so a screen reader hears «Administración, dirección 2».
 */
function AddressList({
  id,
  describedBy,
  label,
  values,
  onChange,
}: {
  id: string;
  describedBy: string | undefined;
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const fields = values.length > 0 ? values : [''];
  // «Añadir otra dirección» puts the cursor in the new field: without it the next keys
  // land on the button and the address is typed nowhere.
  const added = useRef(false);
  const last = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!added.current) return;
    added.current = false;
    last.current?.focus();
  }, [fields.length]);

  return (
    <div className="flex w-full max-w-md flex-col gap-2">
      {fields.map((value, index) => (
        // The position is the identity: addresses have no id of their own and a field
        // being typed in must keep its focus.
        <div key={index} className="flex items-center gap-1.5">
          <input
            ref={index === fields.length - 1 ? last : undefined}
            id={index === 0 ? id : undefined}
            type="email"
            className="field w-full min-w-0"
            aria-label={index === 0 ? undefined : `${label}, dirección ${index + 1}`}
            aria-describedby={index === 0 ? describedBy : undefined}
            value={value}
            onChange={(event) =>
              onChange(fields.map((each, at) => (at === index ? event.target.value : each)))
            }
            placeholder="direccion@ferreteriacubas.com"
          />
          {fields.length > 1 ? (
            <button
              type="button"
              aria-label={`Quitar la dirección ${index + 1} de ${label}`}
              title="Quitar esta dirección"
              onClick={() => onChange(fields.filter((_, at) => at !== index))}
              className="text-ink-3 hover:bg-paper-2 hover:text-ink focus-visible:outline-accent inline-flex size-9 shrink-0 items-center justify-center rounded-md transition-colors focus-visible:outline focus-visible:outline-2"
            >
              <XIcon size={15} />
            </button>
          ) : null}
        </div>
      ))}
      {fields.length < MAX_ADDRESSES ? (
        <Button
          variant="ghost"
          size="sm"
          icon={<PlusIcon size={15} />}
          className="self-start"
          aria-label={`Añadir otra dirección a ${label}`}
          onClick={() => {
            added.current = true;
            onChange([...fields, '']);
          }}
        >
          Añadir otra dirección
        </Button>
      ) : null}
    </div>
  );
}

function DestinationsForm({ activation }: { activation: TriageActivation }) {
  const [draft, setDraft] = useState<Record<string, string[]>>({});
  const save = usePutDestinations();
  useEffect(() => {
    setDraft({ ...(activation.destinations ?? {}) });
  }, [activation.destinations]);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate(compactLists(draft));
  }

  return (
    <Card title="A quién se reenvía cada correo">
      <p className="text-ink-2 text-[14px] leading-relaxed">
        Según el tipo de correo, el sistema decide si tiene que ir a Administración, a María o a
        Paulo, y lo reenvía a las direcciones que pongas aquí. Que se reenvíe de verdad o solo se
        proponga se decide en «Activación».
      </p>
      <form onSubmit={submit} className="flex flex-col">
        {ADDRESSABLE.map((destination) => (
          <Row
            key={destination}
            label={DESTINATION_LABELS[destination]}
            hint={DESTINATION_HINTS[destination]}
          >
            {({ id, describedBy }) => (
              <AddressList
                id={id}
                describedBy={describedBy}
                label={DESTINATION_LABELS[destination]}
                values={draft[destination] ?? []}
                onChange={(next) => setDraft((current) => ({ ...current, [destination]: next }))}
              />
            )}
          </Row>
        ))}
        <p className="text-ink-3 pt-3 text-[13px] leading-snug">
          Un correo se reenvía a todas las direcciones de su destino, hasta {MAX_ADDRESSES}. Un
          destino sin dirección no se reenvía: el correo se queda en su buzón con la propuesta.
        </p>
        <SaveRow
          label="Guardar destinos"
          pending={save.isPending}
          saved={save.isSuccess}
          error={save.isError ? save.error : null}
        />
      </form>
    </Card>
  );
}

function FoldersForm({ activation }: { activation: TriageActivation }) {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const save = usePutFolders();
  useEffect(() => {
    setDraft({ ...(activation.folders ?? {}) });
  }, [activation.folders]);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate(compact(draft));
  }

  return (
    <Card title="En qué carpeta del buzón se guarda una copia">
      <p className="text-ink-2 text-[14px] leading-relaxed">
        Cada correo clasificado se copia a una carpeta de su propio buzón, se reenvíe o no, para que
        luego se encuentre por tipo. Aquí eliges el nombre de la carpeta de cada tipo de correo; la
        carpeta se crea sola la primera vez que hace falta.
      </p>
      <form onSubmit={submit} className="flex flex-col">
        {CATEGORIES.map((category: EmailCategory) => (
          <Row key={category} label={CATEGORY_LABELS[category]}>
            {({ id }) => (
              <input
                id={id}
                className="field w-full max-w-md"
                value={draft[category] ?? ''}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, [category]: event.target.value }))
                }
                placeholder="sin carpeta"
              />
            )}
          </Row>
        ))}
        <p className="text-ink-3 pt-3 text-[13px] leading-snug">
          En blanco, ese tipo de correo no se copia a ninguna carpeta. Sin barras ni puntos en el
          nombre. Cambiar un nombre no toca la carpeta antigua ni lo que ya hay en ella.
        </p>
        <SaveRow
          label="Guardar carpetas"
          pending={save.isPending}
          saved={save.isSuccess}
          error={save.isError ? save.error : null}
        />
      </form>
    </Card>
  );
}
